import React from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Lock, FileText, Award, Check, Mail } from "lucide-react";
import { CONTACT_EMAIL } from "../lib/contact";

const TIERS = [
  {
    id: "professional",
    name: "Professional",
    blurb: "For single agencies ready to make resilience training standard practice.",
    points: ["All four training modules", "Agency dashboard & reports", "SOS silent support", "CEU certificates"],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    blurb: "Multi-agency departments and county-wide programs.",
    points: ["Everything in Professional", "Multi-agency roll-ups", "Priority clinical review", "Dedicated success manager"],
  },
  {
    id: "government",
    name: "Government",
    blurb: "State and federal procurement with compliance requirements.",
    points: ["Everything in Enterprise", "Procurement-ready contracting", "Custom data governance", "Volume licensing"],
  },
];

/** Shown to trial users whose trial has expired. History and certificates stay
 *  reachable; launching new scenarios is blocked server-side. */
export const UpgradeScreen = () => (
  <div className="haruki-fade-in" data-testid="upgrade-screen">
    <div className="mb-6 flex items-center gap-3 rounded-md border px-4 py-3"
         style={{ borderColor: "rgb(220, 80, 60)", background: "rgba(220, 80, 60, 0.08)" }}>
      <Lock className="h-4 w-4 shrink-0" style={{ color: "rgb(220, 80, 60)" }} />
      <div className="text-sm">
        Your 21-day trial has ended. Your completed history and certificates remain available below.
      </div>
    </div>

    <h1 className="font-heading text-2xl font-bold sm:text-3xl">Continue your training</h1>
    <p className="mt-2 text-sm text-muted-foreground">
      Choose a plan to unlock all four modules, agency reporting, and SOS support.
    </p>

    <div className="mt-6 space-y-4">
      {TIERS.map((t) => (
        <Card key={t.id} data-testid={`upgrade-tier-${t.id}`}>
          <CardContent className="p-5">
            <div className="font-heading text-lg font-semibold">{t.name}</div>
            <p className="mt-1 text-sm text-muted-foreground">{t.blurb}</p>
            <ul className="mt-3 space-y-1.5">
              {t.points.map((p) => (
                <li key={p} className="flex items-center gap-2 text-sm">
                  <Check className="h-3.5 w-3.5 shrink-0" style={{ color: "hsl(var(--haruki-leaf))" }} />
                  {p}
                </li>
              ))}
            </ul>
            <Button
              asChild size="lg" className="mt-4 w-full font-semibold"
              style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}
              data-testid={`upgrade-contact-${t.id}`}
            >
              <a href={`mailto:${CONTACT_EMAIL}?subject=Haruki%20Health%20${t.name}%20plan`}>
                <Mail className="mr-2 h-4 w-4" /> Talk to our team
              </a>
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>

    <div className="mt-8 flex gap-3">
      <Button asChild variant="outline" className="flex-1" data-testid="upgrade-view-history">
        <Link to="/app/history"><FileText className="mr-2 h-4 w-4" /> View history</Link>
      </Button>
      <Button asChild variant="outline" className="flex-1" data-testid="upgrade-view-certificates">
        <Link to="/app/certificates"><Award className="mr-2 h-4 w-4" /> Certificates</Link>
      </Button>
    </div>
  </div>
);

export default UpgradeScreen;
